import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { MonoLabel, StatusBadge, StatusDot } from "@/components/primitives";
import { OUTCOMES } from "@/lib/demo-data";
import type { OutcomeRow, OutcomeStatus } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useSEO } from "@/lib/useSEO";
import { PageShell } from "@/components/PageShell";

type Filter = "ALL" | OutcomeStatus;
const FILTERS: Filter[] = ["ALL", "ACTIVE", "VERIFIED", "FAILED", "DISPUTED"];
type Sort = "NEWEST" | "IMPACT";

function Row({ o, expanded, onToggle }: { o: OutcomeRow; expanded: boolean; onToggle: () => void }) {
  return (
    <>
      <button
        data-testid={`outcome-row-${o.id}`}
        onClick={onToggle}
        aria-expanded={expanded}
        className="grid w-full grid-cols-[1fr_auto] items-center gap-3 border-b border-line/60 px-4 py-4 text-left transition-colors duration-150 hover:bg-cyan/5 md:grid-cols-[100px_140px_1fr_90px_110px_100px_120px_24px] md:px-5"
      >
        <span className="font-mono text-xs text-cyan">{o.id}</span>
        <span className="hidden font-mono text-xs text-dim md:block">{o.actor}</span>
        <span className="hidden font-display text-sm font-semibold text-ink md:block">{o.objective}</span>
        <span className="hidden font-mono text-xs text-dim md:block">{o.target}</span>
        <span className="hidden font-mono text-xs text-ink md:block">{o.actual}</span>
        <span className={cn("hidden font-mono text-xs md:block", o.impact > 0 ? "text-success" : "text-dim")}>
          {o.impactLabel}
        </span>
        <span className="hidden md:block">
          <StatusBadge status={o.status} />
        </span>
        <ChevronDown
          className={cn("h-4 w-4 text-dim transition-transform duration-200", expanded && "rotate-180")}
          aria-hidden="true"
        />
        {/* mobile summary */}
        <span className="col-span-2 flex items-center justify-between md:hidden">
          <span className="flex flex-col">
            <span className="font-display text-sm font-semibold text-ink">{o.objective}</span>
            <span className="font-mono text-[10px] text-dim">
              {o.actor} · {o.actual} · {o.impactLabel}
            </span>
          </span>
          <StatusBadge status={o.status} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-line/60 bg-base/60"
          >
            <div className="grid gap-4 px-5 py-4 sm:grid-cols-4" data-testid={`outcome-detail-${o.id}`}>
              {[
                ["EVIDENCE", o.evidence],
                ["EPOCH", o.epoch],
                ["WINDOW", o.opened],
                ["MEASUREMENT", "PER CONTRACT SPEC"],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col gap-1">
                  <MonoLabel className="text-[8px]">{k}</MonoLabel>
                  <span className="font-mono text-xs text-dim">{v}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function Outcomes() {
  useSEO({
    title: "Outcome Registry — Consequence Protocol",
    description:
      "A registry of verifiable outcomes: claims, targets, measured results, and settled consequences. Demo data for interface demonstration.",
    path: "/outcomes",
  });

  const [filter, setFilter] = useState<Filter>("ALL");
  const [sort, setSort] = useState<Sort>("NEWEST");
  const [expanded, setExpanded] = useState<string | null>(null);

  const rows = useMemo(() => {
    const filtered = filter === "ALL" ? OUTCOMES : OUTCOMES.filter((o) => o.status === filter);
    return [...filtered].sort((a, b) => (sort === "IMPACT" ? b.impact - a.impact : b.id.localeCompare(a.id)));
  }, [filter, sort]);

  return (
    <PageShell testId="page-outcomes">
      <section className="container-t pb-12 pt-20 sm:pt-28">
        <SectionHeader
          index="00"
          label="Outcome Registry"
          title={
            <>
              Every claim leaves a <span className="text-cyan">record</span>
            </>
          }
          description="The registry is the protocol’s public memory: outcome contracts, their measured results, and the verdicts that settled them. Filter by state, sort by impact."
        />
        <Reveal>
          <div className="mt-8 inline-flex items-center gap-2 border border-warn/40 bg-warn/10 px-3 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-warn" />
            <MonoLabel className="text-[9px] text-warn">Demo data — simulated registry for interface demonstration</MonoLabel>
          </div>
        </Reveal>
      </section>

      <section className="container-t pb-28" data-testid="registry">
        <Reveal>
          <div className="panel hud-corners">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line px-4 py-3 sm:px-5">
              <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter outcomes">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    role="tab"
                    aria-selected={filter === f}
                    data-testid={`outcome-filter-${f.toLowerCase()}`}
                    onClick={() => setFilter(f)}
                    className={cn(
                      "border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200",
                      filter === f
                        ? "border-cyan/60 bg-cyan/10 text-cyan"
                        : "border-line text-dim hover:border-electric/40 hover:text-ink"
                    )}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <MonoLabel className="hidden text-[9px] sm:block">SORT</MonoLabel>
                {(["NEWEST", "IMPACT"] as Sort[]).map((s) => (
                  <button
                    key={s}
                    data-testid={`outcome-sort-${s.toLowerCase()}`}
                    onClick={() => setSort(s)}
                    className={cn(
                      "border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200",
                      sort === s
                        ? "border-electric/60 bg-electric/10 text-electric"
                        : "border-line text-dim hover:border-electric/40 hover:text-ink"
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Table header (desktop) */}
            <div className="hidden grid-cols-[100px_140px_1fr_90px_110px_100px_120px_24px] border-b border-line px-5 py-2.5 md:grid">
              {["OUTCOME ID", "ACTOR", "OBJECTIVE", "TARGET", "ACTUAL", "IMPACT", "STATUS", ""].map((h) => (
                <MonoLabel key={h} className="text-[8px]">{h}</MonoLabel>
              ))}
            </div>

            <div data-testid="registry-rows">
              {rows.map((o) => (
                <Row key={o.id} o={o} expanded={expanded === o.id} onToggle={() => setExpanded(expanded === o.id ? null : o.id)} />
              ))}
              {rows.length === 0 && (
                <div className="px-5 py-10 text-center">
                  <MonoLabel>No outcomes match this filter</MonoLabel>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-line px-5 py-3">
              <MonoLabel className="text-[8px] text-dim/60">Simulated entries — no live protocol data</MonoLabel>
              <MonoLabel className="text-[8px]">CONSEQ.REGISTRY v0.1</MonoLabel>
            </div>
          </div>
        </Reveal>
      </section>
    </PageShell>
  );
}
